import NextLink from 'next/link';

import cx from 'classnames';

import { type Masked, unmask } from '~/contentful/graphql';
import { flattenCollection } from '~/contentful/utils/collection';
import { AnalyticsCard, AnalyticsContent } from '~/ui/components/analytics';
import { BaseCard, CardSize, type CommonCardProps } from '~/ui/components/cards/base';
import { LinkContent } from '~/ui/components/link';
import typographyStyles from '~/ui/styles/typography.module.scss';
import { getInternalLinkFromFragment } from '~/ui/systems/internalLink';

import styles from './feature.module.scss';
import { type FeatureCard_FeatureFragment } from './query';
import { type Hero_HeroAssetFragment } from '../../heroAsset/query';
import { Image } from '../../image';

export interface FeatureCardProps extends CommonCardProps {
  data: Masked<typeof FeatureCard_FeatureFragment>;
}

export function FeatureCard({ data, module, ...props }: FeatureCardProps) {
  const feature = unmask<typeof FeatureCard_FeatureFragment>(data);
  const { previewVideo, heroImage, dateString, name, pinnedGrantStoriesCollection } = feature;

  const isHighlightedFeature = !!pinnedGrantStoriesCollection?.items.length;

  const pinnedGrantStories = flattenCollection(pinnedGrantStoriesCollection);

  const heroVideoData = previewVideo
    ? {
        __typename: 'HeroAsset',
        video: { ...previewVideo },
      }
    : null;

  const relatedContent = isHighlightedFeature ? (
    <div className={styles.relatedContent}>
      {pinnedGrantStories.map((relatedItem, index) => {
        const { title, __typename } = relatedItem;

        const isArticle = __typename === 'Article';

        const label = isArticle ? 'article' : 'grant story';
        const image = isArticle
          ? (relatedItem.hero?.image ?? relatedItem.heroImage)
          : relatedItem.leadImage;
        return (
          <NextLink
            key={index}
            href={getInternalLinkFromFragment(relatedItem)}
            className={styles.relatedLinkWrapper}
            aria-label={`Read ${label} for ${title}`}
          >
            <div className={styles.relatedItem}>
              <div className={styles.relatedLeft}>
                <div className={cx(styles.relatedTitle, typographyStyles.bodyLarge)}>{title}</div>
                <div className={cx(styles.itemCta, typographyStyles.bodySmall)}>
                  <LinkContent withIcon href={getInternalLinkFromFragment(relatedItem)}>
                    Read the {label}
                  </LinkContent>
                </div>
              </div>
              <div className={styles.relatedRight}>
                {image && <Image image={image} className={styles.relatedImage} />}
              </div>
            </div>
          </NextLink>
        );
      })}
    </div>
  ) : undefined;

  return (
    <BaseCard
      hero={(heroVideoData as Masked<typeof Hero_HeroAssetFragment>) ?? heroImage ?? undefined}
      title={name ?? ''}
      isTitleLarge
      disableMaxHeight
      description={!isHighlightedFeature ? dateString || undefined : undefined}
      cta={isHighlightedFeature ? 'Explore the feature' : 'Explore the Special Feature'}
      href={getInternalLinkFromFragment(feature)}
      event={{
        cardModule: module,
        cardType: AnalyticsCard.FEATURE,
        cardContentType: AnalyticsContent.FEATURE,
      }}
      ctaAsButton={isHighlightedFeature}
      size={CardSize.DEFAULT}
      middleSlot={relatedContent}
      {...props}
    />
  );
}
